"use client";

import { getBaseUrl } from "@/util/util";
import React, { useEffect, useMemo, useState } from "react";
import "../survey-form.css";
import type { AnswerData, QuestionConfig, SliderField, SliderUnitOption } from "../types";
import CheckboxQuestion from "./CheckboxQuestion";
import ConsentQuestion from "./ConsentQuestion";
import DateQuestion from "./DateQuestion";
import EmailQuestion from "./EmailQuestion";
import MatrixQuestion from "./MatrixQuestion";
import MultiSelectQuestion from "./MultiSelectQuestion";
import RadioQuestion from "./RadioQuestion";
import SliderQuestion from "./SliderQuestion";
import TextInputQuestion from "./TextInputQuestion";
import { useTranslation } from "@/app/i18n/client";


// NOTE: This is a presentational container for survey questions matching the provided design.
// Props kept minimal; can be expanded later to integrate with dynamic survey engine.

// Sentinel stored as the answer of a ShortText question when the user ticks the
// hard-coded "ไม่แพ้" (No allergy) checkbox in the optional category. Treated as a
// valid (answered) response by category-page validation.
export const NO_ALLERGY_MARKER = '__no_allergy__';

type CategoryIconOverride = { left?: number | string; top?: number | string; transform?: string };
type CategoryIconConfig = { url: string; style?: CategoryIconOverride };

interface QuestionFormatProps {
	step?: number;
	totalSteps?: number;
	totalStepsPerCategory?: number[]; // new: individual total steps per category
	questionText: string;
	questionType: 'multiselect' | 'text' | 'radio' | 'email' | 'date' | 'slider' | 'matrix' | 'checkbox' | 'dropdown' | 'consent';
	options?: Array<{ value: string; label: string; imageUrl?: string; labelEn?: string }> | string[]; // for radio & multiselect - support both object and string formats
	value?: string[]; // internal representation (text/radio: single-element array, multiselect: array of selections)
    fields?: Array<{
        name?: string;  // mock data format
        id?: string;    // database format
        label: string;
        labelEn?: string;
	min?: number;
	max?: number;
        step?: number;
        unit?: string;
	units?: SliderUnitOption[];
	placeholder?: string;
	placeholderEn?: string;
	disableBar?: boolean;
	disableMinMax?: boolean;
    }>; // for slider - supports both mock and database formats
	rows?: Array<{ value: string; label: string; labelEn?: string }>; // for matrix
	columns?: Array<{ value: string; label: string; labelEn?: string }>; // for matrix
	onChange?: (values: string[]) => void;
	placeholder?: string;
	totalCategories?: number;
	activeCategoryIndex?: number;
    activeCategoryName?: string;
	completedStepsPerCategory?: number[];
	// Track which categories have been visited (initialized)
	visitedCategoryIndices?: number[];
	// Callback when user clicks on a progress bar
	onProgressBarClick?: (categoryIndex: number) => void;
	mascotImageUrl?: string | null;
	mascotMobileImageUrl?: string | null;
	mascotAlt?: string;
	categoryIconUrls?: Array<string | CategoryIconConfig>; // icons or config objects
	categoryIconOverrides?: CategoryIconOverride[]; // per-icon style overrides (takes precedence over config.style)
	// NEW: Pass through config properties
	layout?: 'horizontal' | 'vertical';
	maxSelections?: number;
	minSelections?: number;
	showCounter?: boolean;
	showImages?: boolean;
	multiline?: boolean;
    otherConfig?: QuestionConfig;
	// Ranking-specific props
	enableRanking?: boolean;
	rankingLimit?: number;
	// Consent-specific props
	consentText?: string;
	consentTextEn?: string;
	consentCheckboxes?: Array<{
		id: string;
		label: string;
		labelEn?: string;
		isRequired: boolean;
	}>;
	// User email from session
	userEmail?: string | null;
	// Keyboard support
	onEnterPress?: () => void; // Callback when Enter is pressed in text inputs
	// Validation rules for questions
	validationRules?: any;
	// Hard-coded behavior: when active category is optional, slider questions show a "ไม่ทราบผล" checkbox
	isOptionalCategory?: boolean;
}

const defaultOptions = [
	"Vitamin A",
	"Vitamin B",
	"Vitamin C",
	"Vitamin D",
	"Vitamin E",
	"Vitamin K",
	"Vitamin รวม",
	"Zinc",
	"Iron",
	"Magnesium",
	"Calcium",
	"Fish Oil",
];

// Default category icon configs (mirrors CATEGORY_ICON_MAP) with optional style placeholders
const DEFAULT_CATEGORY_ICON_MAP: CategoryIconConfig[] = [
	{ url: "https://dlzhyrs75lk5e.cloudfront.net/customer-cra/cms/galleryComponent/2025/11/5/690ac1e1b09f24eb4e4f6d01", style: {left: "60%", top: "50%", transform: "translate(-50%, -50%) translate(-160px, -240px)"} },
	{ url: "https://dlzhyrs75lk5e.cloudfront.net/customer-cra/cms/galleryComponent/2025/11/5/690ac1ecb09f24eb4e4f6d07", style: {left: "60%", top: "50%", transform: "translate(-50%, -50%) translate(-220px, -160px)"} },
	{ url: "https://dlzhyrs75lk5e.cloudfront.net/customer-cra/cms/galleryComponent/2025/11/5/690ac1fab09f24eb4e4f6d0d", style: {left: "60%", top: "50%", transform: "translate(-50%, -50%) translate(-225px, -70px)"} },
	{ url: "https://dlzhyrs75lk5e.cloudfront.net/customer-cra/cms/galleryComponent/2025/11/5/690ac21bb09f24eb4e4f6d19", style: {left: "60%", top: "50%", transform: "translate(-50%, -50%) translate(-200px, 15px)"} },
	{ url: "https://dlzhyrs75lk5e.cloudfront.net/customer-cra/cms/galleryComponent/2025/11/5/690ac353b09f24eb4e4f6d31", style: {left: "60%", top: "50%", transform: "translate(-50%, -50%) translate(60px, -240px)"} },
	{ url: "https://dlzhyrs75lk5e.cloudfront.net/customer-cra/cms/galleryComponent/2025/11/5/690ac22db09f24eb4e4f6d1f", style: {left: "60%", top: "50%", transform: "translate(-50%, -50%) translate(110px, -160px)"} },
	{ url: "https://dlzhyrs75lk5e.cloudfront.net/customer-cra/cms/galleryComponent/2025/11/5/690ac23fb09f24eb4e4f6d25", style: {left: "60%", top: "50%", transform: "translate(-50%, -50%) translate(120px, -75px)"} },
	{ url: "https://dlzhyrs75lk5e.cloudfront.net/customer-cra/cms/galleryComponent/2025/11/5/690ac251b09f24eb4e4f6d2b", style: {left: "60%", top: "50%", transform: "translate(-50%, -50%) translate(100px, 10px)"} },
];

export default function QuestionFormat({
	step = 1,
	totalSteps = 9,
	totalStepsPerCategory = [],
	questionText,
	questionType,
	options = defaultOptions,
	value,
    fields,
	rows,
	columns,
	onChange,
	placeholder,
	totalCategories = 1,
	activeCategoryIndex = 0,
    activeCategoryName = "",
	completedStepsPerCategory = [],
	visitedCategoryIndices = [],
	mascotImageUrl,
	mascotMobileImageUrl,
	mascotAlt = "category mascot",
	categoryIconUrls = DEFAULT_CATEGORY_ICON_MAP,
	categoryIconOverrides = [],
	onProgressBarClick,
	// NEW: Config properties
	layout,
	maxSelections,
	minSelections,
	showCounter,
	showImages,
	multiline,
    otherConfig,
	enableRanking,
	rankingLimit,
	// Consent-specific props
	consentText,
	consentTextEn,
	consentCheckboxes,
	// User email from session
	userEmail,
	// Keyboard support
	onEnterPress,
	// Validation rules
	validationRules,
	isOptionalCategory = false,
}: QuestionFormatProps) {
	// Get current language
	const { lang } = useTranslation();
	
	// Helper function to get text by language
	const getText = (th: string | undefined, en: string | undefined): string => {
		if (lang === 'en') {
			return en || th || '';
		}
		return th || en || '';
	};
	
	// Internal state (uncontrolled mode fallback)
	const [internalSelected, setInternalSelected] = useState<string[]>(value || []);
	const selected = value ?? internalSelected;

	// Responsive scale for mascot area so it always fits viewport while anchored bottom-left
	const [mascotScale, setMascotScale] = useState(1);

	useEffect(() => {
		const BASE_WIDTH = 640; // original design width
		const BASE_HEIGHT = 640; // original design height
		const PADDING = 16; // keep a tiny safety padding
		function computeScale() {
			if (typeof window === 'undefined') return;
			const vw = window.innerWidth - PADDING;
			const vh = window.innerHeight - PADDING;
			// Dynamic scale factor: 40% on narrow (<1024px) screens, 60% otherwise (never upscale beyond 1)
			const scaleFactor = vw < 1024 ? 0.5 : 0.6;
			const targetScaleW = (vw * scaleFactor) / BASE_WIDTH;
			const targetScaleH = (vh * scaleFactor) / BASE_HEIGHT;
			const scale = Math.min(1, targetScaleW, targetScaleH);
			setMascotScale(scale > 0 ? scale : 0.1);
		}
		computeScale();
		window.addEventListener('resize', computeScale);
		return () => window.removeEventListener('resize', computeScale);
    }, []);

	// Build normalized options array with language support
	const normalizedOptions: Array<{ value: string; label: string; imageUrl?: string; labelEn?: string }> = useMemo(() => {
		return (options || []).map((opt) => {
			if (typeof opt === 'string') {
				return { value: opt, label: opt };
			}
			return {
				...opt,
				label: getText(opt.label, opt.labelEn)
			};
		});
	}, [options, getText]);

	// Build normalized fields for sliders with language support
	const normalizedFields = useMemo(() => {
		return (fields || []).map(field => ({
			...field,
			name: field.name || field.id || '',
			label: getText(field.label, field.labelEn)
		}));
	}, [fields, getText]);

	// Build normalized slider fields
	const normalizedSliders = useMemo(() => {
		return (fields || []).map(field => {
			const hasExplicitDefault = Array.isArray(field.units)
				? field.units.some((unit) => unit.isDefault === true)
				: false;

			return {
				...field,
				id: field.id || field.name || '',
				labelEn: field.labelEn || field.label || '',
				label: getText(field.label, field.labelEn),
				placeholder: getText(field.placeholder, field.placeholderEn),
				units: Array.isArray(field.units) && field.units.length > 0
					? field.units.map((unit, index) => ({
						value: unit.value,
						label: unit.label || unit.value,
						labelEn: unit.labelEn || unit.label || unit.value,
						factor: typeof unit.factor === 'number' && unit.factor > 0 ? unit.factor : 1,
						isDefault: unit.isDefault === true || (!hasExplicitDefault && index === 0),
					}))
					: field.unit
						? [{ value: field.unit, label: field.unit, labelEn: field.unit, factor: 1, isDefault: true }]
						: []
			};
		});
	}, [fields, getText]);

	// Build normalized matrix rows with language support
	const normalizedRows = useMemo(() => {
		return (rows || []).map(row => ({
			...row,
			label: getText(row.label, row.labelEn)
		}));
	}, [rows, getText]);

	// Build normalized matrix columns with language support
	const normalizedColumns = useMemo(() => {
		return (columns || []).map(col => ({
			...col,
			label: getText(col.label, col.labelEn)
		}));
	}, [columns, getText]);

	// Build normalized consent checkboxes with language support
	const normalizedCheckboxes = useMemo(() => {
		return (consentCheckboxes || []).map(checkbox => ({
			...checkbox,
			label: getText(checkbox.label, checkbox.labelEn)
		}));
	}, [consentCheckboxes, getText]);

	// Build a QuestionConfig for delegated components; adapt per questionType
	const componentConfig: QuestionConfig = useMemo(() => ({
		// Base layout and display properties
		layout,
		maxSelections,
		minSelections,
		showCounter,
		showImages,
		multiline: multiline ?? false,
		
		// Option-based questions (radio, checkbox, multiselect, dropdown)
		...(
			['radio', 'multiselect', 'checkbox', 'dropdown'].includes(questionType)
				? { options: normalizedOptions }
				: {}
		),

		// Slider bounds and fields
		...(questionType === 'slider' ? {
			min: 0,
			max: 100,
			fields: normalizedFields,
			sliders: normalizedSliders,
		} : {}),

		// Matrix configuration
		...(questionType === 'matrix' ? {
			rows: normalizedRows,
			columns: normalizedColumns,
		} : {}),

		// Text input configuration
		...(questionType === 'text' ? {
			minLength: otherConfig?.minLength,
			maxLength: otherConfig?.maxLength,
			placeholder: otherConfig?.placeholder,
			placeholderEn: otherConfig?.placeholderEn,
		} : {}),

		// Date configuration
		...(questionType === 'date' ? {
			minDate: otherConfig?.minDate,
			maxDate: otherConfig?.maxDate,
			format: otherConfig?.format,
			defaultToToday: otherConfig?.defaultToToday,
			disableWeekends: otherConfig?.disableWeekends,
			disablePastDates: otherConfig?.disablePastDates,
			disableFutureDates: otherConfig?.disableFutureDates,
			helperText: otherConfig?.helperText,
			helperTextEn: otherConfig?.helperTextEn,
		} : {}),

		// Email configuration
		...(questionType === 'email' ? {
			placeholder: lang === 'en' ? otherConfig?.placeholderEn : otherConfig?.placeholder,
			placeholderEn: otherConfig?.placeholderEn,
		} : {}),

		// Checkbox ranking configuration
		...(questionType === 'checkbox' ? {
			enableRanking,
			rankingLimit,
		} : {}),

		// Consent configuration
		...(questionType === 'consent' ? {
			consentText: lang === 'en' ? consentTextEn : consentText,
			consentTextEn,
			checkboxes: normalizedCheckboxes,
		} : {}),
	}), [
		questionType,
		layout,
		maxSelections,
		minSelections,
		showCounter,
		showImages,
		multiline,
		normalizedOptions,
		normalizedFields,
		normalizedSliders,
		normalizedRows,
		normalizedColumns,
		normalizedCheckboxes,
		otherConfig,
		lang,
		enableRanking,
		rankingLimit,
		placeholder,
		consentText,
		consentTextEn,
	]);
    
    const configuredFields: SliderField[] = useMemo(() => {
        return (componentConfig.sliders || []) as SliderField[];
    }, [componentConfig.sliders]);

	const getNormalizedUnits = (field: SliderField): SliderUnitOption[] => {
		if (Array.isArray(field.units) && field.units.length > 0) {
			return field.units;
		}

		if (field.unit) {
			return [{ value: field.unit, label: field.unit, labelEn: field.unit, factor: 1, isDefault: true }];
		}

		return [];
	};

	const getDefaultUnit = (field: SliderField): string => {
		const units = getNormalizedUnits(field);
		return units.find((unit) => unit.isDefault)?.value || units[0]?.value || field.unit || '';
	};

	const isSliderFreeInput = (field: SliderField): boolean => {
		return field.disableBar === true && field.disableMinMax === true;
	};

    
    useEffect(() => {
        if (questionType === 'slider' && selected.length === 0) {
			const defaultSliderValues: Record<string, { value: number; unit: string }> = {};
            
            configuredFields.forEach((field: SliderField) => { 
                const fieldKey = field.name || field.id;
                if (defaultSliderValues[fieldKey] === undefined) {
					defaultSliderValues[fieldKey] = {
						value: isSliderFreeInput(field) ? '' as unknown as number : (field.min ?? 0),
						unit: getDefaultUnit(field),
					};
                }
            });

            const defaultState = [JSON.stringify(defaultSliderValues)];

            if (!value) setInternalSelected(defaultState); 
            onChange?.(defaultState); 
        }

    }, [questionType, selected.length, onChange, value, setInternalSelected, configuredFields]);

	let answerData: AnswerData;
	if (questionType === 'multiselect' || questionType === 'checkbox' || questionType === 'consent') {
		answerData = { values: selected };
	} else if (questionType === 'matrix') {
		// Expect selected[0] to contain a JSON string of the matrix mapping { [rowValue]: columnValue }
		let matrixValues: Record<string, string> = {};
		if (selected[0]) {
			try {
				matrixValues = JSON.parse(selected[0]);
			} catch {
				matrixValues = {};
			}
		}
		answerData = { matrix: matrixValues };
	} else if (questionType === 'slider') {
		// For slider with multiple fields, store as object keyed by field name
		// Parse selected as JSON object if it exists, otherwise use empty object
		let sliderValues: Record<string, { value: number | string; unit: string; isAnswered?: boolean }> = {};
		if (selected[0]) {
			try {
				sliderValues = JSON.parse(selected[0]);
			} catch {
				// If not JSON, treat as empty
				sliderValues = {};
			}
        }

        configuredFields.forEach((field: SliderField) => {
            const fieldKey = field.name || field.id;
            if (sliderValues[fieldKey] === undefined) {
				sliderValues[fieldKey] = {
					value: isSliderFreeInput(field) ? '' : (field.min ?? 0),
					unit: getDefaultUnit(field),
					isAnswered: false,
				};
            }
        });
		answerData = { value: sliderValues };
	} else if (questionType === 'date' || questionType === 'email' || questionType === 'text' || questionType === 'radio') {
		answerData = { value: selected[0] || '' };
	} else {
		answerData = { value: selected[0] || '' };
    }

	const sliderValues: Record<string, { value: number | string; unit: string; isAnswered?: boolean }> = useMemo(() => {
		let parsed: Record<string, any> = {};

        if (selected[0]) {
            try {
                parsed = JSON.parse(selected[0]);
            } catch {
                parsed = {};
            }
        }

        configuredFields.forEach((field: SliderField) => {
            const key = field.name || field.id;
			const defaultUnit = getDefaultUnit(field);
			const rawValue = parsed[key];

			if (rawValue && typeof rawValue === 'object' && !Array.isArray(rawValue)) {
				const fallbackValue = isSliderFreeInput(field) ? '' : (field.min ?? 0);
				const isAnswered =
					typeof rawValue.isAnswered === 'boolean'
						? rawValue.isAnswered
						: true;
				// ถ้า user ลบจนว่าง (value === '') → คงไว้เป็น '' ไม่ reset เป็น min
				// ถ้าไม่ได้ set (undefined/null) หรือ invalid → ใช้ fallback (min หรือ '')
				const existingValue = rawValue.value;
				// Keep partial decimal strings (trailing dot / trailing zero after a dot)
				// untouched so the user can keep typing them.
				const isPartialDecimal =
					typeof existingValue === 'string' &&
					(existingValue.endsWith('.') || existingValue === '-' || /\.\d*0$/.test(existingValue));
				parsed[key] = {
					value: existingValue === ''
						? ''
						: isPartialDecimal
							? existingValue
							: (existingValue === undefined || existingValue === null || isNaN(Number(existingValue))
								? fallbackValue
								: Number(existingValue)),
					unit: typeof rawValue.unit === 'string' && rawValue.unit !== '' ? rawValue.unit : defaultUnit,
					isAnswered,
				};
				return;
			}

			const fallbackValue = isSliderFreeInput(field) ? '' : (field.min ?? 0);
			parsed[key] = {
				// primitive '' ถือว่า user ไม่ได้ set → ใช้ fallback เหมือนเดิม (กรณีนี้เกิดน้อยมาก)
				value: rawValue === undefined || rawValue === null || rawValue === '' || isNaN(Number(rawValue)) ? fallbackValue : Number(rawValue),
				unit: defaultUnit,
				isAnswered: false,
			};
        });

        return parsed;
    }, [selected, configuredFields]);
        


    const handleAnswerChange = (ad: AnswerData, fieldName?: string) => {
        let newValues: string[];
        if (questionType === 'multiselect' || questionType === 'checkbox' || questionType === 'consent') {
            newValues = Array.isArray(ad.values) ? ad.values.map(String) : [];
        } else if (questionType === 'matrix') {
            newValues = [JSON.stringify(ad.matrix || {})]
        } else if (questionType === 'slider' && fieldName) {
			const fieldConfig = configuredFields.find((field) => (field.name || field.id) === fieldName);
			const updatedField: Record<string, any> = { ...sliderValues };
			// User actively edited a slider field — drop the "ไม่ทราบผล" marker so the answer is treated as real.
			delete updatedField.__skipped;
			// Preserve partial decimal entries as raw strings (e.g. "1." or "1.50")
			// so the user can finish typing; SliderQuestion coerces to a number on blur.
			const isPartialDecimal =
				typeof ad.value === 'string' &&
				(ad.value.endsWith('.') || ad.value === '-' || /\.\d*0$/.test(ad.value));
			updatedField[fieldName] = {
				value: ad.value === '' ? '' : isPartialDecimal ? ad.value : typeof ad.value === 'number' ? ad.value : Number(ad.value),
				unit: typeof ad.unit === 'string' && ad.unit !== '' ? ad.unit : (fieldConfig ? getDefaultUnit(fieldConfig) : ''),
				isAnswered: true,
			};
            newValues = [JSON.stringify(updatedField)]
        } else {
            newValues = ad.value !== null && ad.value !== '' ? [String(ad.value)] : []
        }

        if (!value) setInternalSelected(newValues)
        onChange?.(newValues)
        
    }

	// Determine per-category total (fallback to global totalSteps if array not provided)
	const activeTotalForCategory = totalStepsPerCategory[activeCategoryIndex] ?? totalSteps;
	// Use answered count (completedStepsPerCategory) for progress to avoid marking 100% before last question answered
	const answeredActive = completedStepsPerCategory[activeCategoryIndex] ?? 0;
	const progressPercent = Math.min(100, Math.max(0, (answeredActive / activeTotalForCategory) * 100));

	// Build an array describing each category's bar fill percent respecting individual totals, always based on answered counts
	const categoryProgressArray = useMemo(() => {
		return Array.from({ length: totalCategories }).map((_, idx) => {
			const totalForIdx = totalStepsPerCategory[idx] ?? totalSteps;
			const answered = completedStepsPerCategory[idx] ?? 0;
			return Math.min(100, Math.max(0, (answered / totalForIdx) * 100));
		});
    }, [totalCategories, completedStepsPerCategory, activeCategoryIndex, totalStepsPerCategory, totalSteps]);

	// Determine which categories have been visited (initialized)
	const categoryVisitedStatus = useMemo(() => {
		return Array.from({ length: totalCategories }).map((_, idx) => {
			// Category is visited if it's in the visitedCategoryIndices array
			return visitedCategoryIndices.includes(idx);
		});
	}, [totalCategories, visitedCategoryIndices]);

	// Handle progress bar click
	const handleProgressBarClick = (categoryIndex: number) => {
		// ตรวจสอบว่า category นี้เคยเปิดแล้วหรือยัง (visited/initialized)
		const isVisited = categoryVisitedStatus[categoryIndex];
		
		// ถ้ายังไม่เคยเปิด ไม่ให้คลิกได้ (ยกเว้น category แรกคลิกได้เสมอ)
		if (!isVisited && categoryIndex !== 0) {
			return;
		}
		
		// เรียก callback เพื่อกลับไปหน้าหลัก (รวมถึงกรณีคลิก category ปัจจุบันเพื่อกลับไปที่การ์ด)
		if (onProgressBarClick) {
			onProgressBarClick(categoryIndex);
		}
	};

	// Icon positions hugging the edge of the background circle (first 4 left, next 4 right)
	const iconPositions = useMemo(() => {
		const circleDiameter = 400; // match bg circle size
		const circleRadius = circleDiameter / 2; // 200
		const iconSize = 64; // approx w-16
		const iconDistance = circleRadius + iconSize / 2; // centers so edge touches circle
		// Circle is top-aligned inside a 500px mascot layer (image height). Adjust center offset.
		const mascotLayerHeight = 500;
		const centerYOffset = -(mascotLayerHeight / 2 - circleRadius); // shift from layer center to circle center
		// Vertical offsets relative to circle center for 4 icons each side
		const yOffsets = [-circleRadius + iconSize / 2, -100, 100, circleRadius - iconSize / 2];
		return categoryIconUrls.map((entry, idx) => {
			const isLeft = idx < 4;
			const columnIndex = isLeft ? idx : idx - 4;
			const x = (isLeft ? -1 : 1) * iconDistance;
			const y = (yOffsets[columnIndex] ?? 0) + centerYOffset;
			return { x, y };
		});
    }, [categoryIconUrls]);

	const renderQuestion = (): JSX.Element | null => {
		switch (questionType) {
			case 'text': {
				// "ไม่แพ้" hardcoded option — only visible when this question lives under the
				// optional category. When ticked the text field is cleared, disabled, and the
				// answer is stored as the NO_ALLERGY_MARKER so the user can proceed without typing.
				const textSkipped = selected[0] === NO_ALLERGY_MARKER;
				const displayedTextValue: AnswerData = textSkipped ? { value: '' } : answerData;

				const handleNoAllergyToggle = (skipped: boolean) => {
					const newValues = skipped ? [NO_ALLERGY_MARKER] : [];
					if (!value) setInternalSelected(newValues);
					onChange?.(newValues);
				};

				return (
					<div className="w-full flex flex-col items-center gap-4">
						<TextInputQuestion
							config={componentConfig}
							value={displayedTextValue}
							onChange={handleAnswerChange}
							onEnterPress={onEnterPress}
							disabled={textSkipped}
						/>
						{isOptionalCategory && (
							<div className="w-full flex items-center justify-center">
								<label
									htmlFor="text-skip-no-allergy"
									className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border cursor-pointer select-none transition-colors ${textSkipped ? 'bg-white border-[#2A807B] text-[#2A807B]' : 'bg-[#2A807B] border-[#2A807B] text-white'}`}
								>
									<input
										id="text-skip-no-allergy"
										type="checkbox"
										checked={textSkipped}
										onChange={(e) => handleNoAllergyToggle(e.target.checked)}
										className="w-4 h-4 accent-[#2A807B]"
									/>
									<span className="text-sm font-medium">{lang === 'en' ? 'No allergy' : 'ไม่แพ้'}</span>
								</label>
							</div>
						)}
					</div>
				);
			}
			case 'radio':
				return (
					<RadioQuestion
						config={componentConfig}
						value={answerData}
						onChange={handleAnswerChange}
						onEnterPress={onEnterPress}
					/>
				);
			case 'checkbox':
				return (
					<CheckboxQuestion
						config={componentConfig}
						value={answerData}
						onChange={handleAnswerChange}
						onEnterPress={onEnterPress}
					/>
				);
			case 'multiselect':
				return (
					<MultiSelectQuestion
						config={componentConfig}
						value={answerData}
						onChange={handleAnswerChange}
					/>
				);
			case 'email':
				return (
					<EmailQuestion
						config={componentConfig}
						value={answerData}
						onChange={handleAnswerChange}
						validationRules={validationRules}
						userEmail={userEmail}
						onEnterPress={onEnterPress}
					/>
				);
			case 'date':
				return (
					<DateQuestion
						config={componentConfig}
						value={{ value: answerData.value }}
						onChange={(v) => handleAnswerChange({ value: v.value })}
						onEnterPress={onEnterPress}
					/>
				);
			case 'matrix':
				return (
					<MatrixQuestion
						config={componentConfig}
						value={answerData}
						onChange={(v) => handleAnswerChange(v)}
						onEnterPress={onEnterPress}
					/>
				);
			case 'slider': {
                // "ไม่ทราบผล" hardcoded option — only visible when this question lives under the optional category.
                // When ticked: all slider fields are flagged isAnswered=false in the saved answer, so backend
                // rule evaluation treats them as null (see workflow-execution.service.ts).
                let parsedSliderAnswer: any = {};
                if (selected[0]) {
                    try { parsedSliderAnswer = JSON.parse(selected[0]); } catch { parsedSliderAnswer = {}; }
                }
                const sliderSkipped = parsedSliderAnswer?.__skipped === true;

                const handleSliderSkipToggle = (skipped: boolean) => {
                    let parsed: Record<string, any> = {};
                    if (selected[0]) {
                        try { parsed = JSON.parse(selected[0]); } catch { parsed = {}; }
                    }
                    configuredFields.forEach((field: SliderField) => {
                        const key = field.name || field.id;
                        if (skipped) {
                            // "ไม่ทราบผล" turned ON: clear any value the user typed and mark unanswered.
                            parsed[key] = {
                                value: '',
                                unit: getDefaultUnit(field),
                                isAnswered: false,
                            };
                        } else if (!parsed[key] || typeof parsed[key] !== 'object') {
                            // "ไม่ทราบผล" turned OFF and no prior state — re-seed the default so the
                            // SliderQuestion has something to render.
                            parsed[key] = {
                                value: isSliderFreeInput(field) ? '' : (field.min ?? 0),
                                unit: getDefaultUnit(field),
                                isAnswered: false,
                            };
                        }
                    });
                    if (skipped) {
                        parsed.__skipped = true;
                    } else {
                        delete parsed.__skipped;
                    }
                    const newValues = [JSON.stringify(parsed)];
                    if (!value) setInternalSelected(newValues);
                    onChange?.(newValues);
                };

                // Support both 'fields' (mock data) and 'sliders' (database) format
                return configuredFields.length > 0 ? (
                    <React.Fragment>
                        {configuredFields.map((field: SliderField, index: number) => {
                            const fieldKey = field.name || field.id;
                            const safeValue = sliderValues[fieldKey];
                            const isLastField = index === configuredFields.length - 1;
                            // Create a unique ID based on field key for reliable element selection
                            const fieldId = `slider-field-${fieldKey}`;
                            
                            const handleFocusNext = () => {
                                // Focus next slider within this question group using ID for accurate selection
                                const nextIndex = index + 1;
                                const nextField = configuredFields[nextIndex];
                                if (nextField) {
                                    const nextFieldKey = nextField.name || nextField.id;
                                    const nextFieldId = `slider-field-${nextFieldKey}`;
                                    // Try to find visible input (desktop or mobile)
                                    const desktopInput = document.getElementById(`${nextFieldId}-desktop`) as HTMLInputElement;
                                    const mobileInput = document.getElementById(`${nextFieldId}-mobile`) as HTMLInputElement;
                                    
                                    // Focus whichever is visible
                                    const nextInput = desktopInput?.offsetParent !== null ? desktopInput : mobileInput;
                                    
                                    if (nextInput && nextInput.offsetParent !== null) {
                                        nextInput.focus();
                                    }
                                }
                            };
                            
                            return (
                                <SliderQuestion
                                    key={fieldKey}
                                    label={field.label}
									config={{ ...componentConfig, unit: field.unit, units: field.units, min: field.min, max: field.max, step: field.step, placeholder: field.placeholder, placeholderEn: field.placeholderEn, disableBar: field.disableBar, disableMinMax: field.disableMinMax }}
									value={safeValue}
                                    onChange={(v) => handleAnswerChange(v, fieldKey)}
                                    onEnterPress={isLastField ? onEnterPress : undefined}
                                    isLastField={isLastField}
                                    onFocusNextField={!isLastField ? handleFocusNext : undefined}
                                    sliderFieldId={fieldId}
                                    disabled={sliderSkipped}
                                />
                            );
                        })}
                        {isOptionalCategory && (
                            <div className="w-full flex items-center justify-center pt-4">
                                <label
                                    htmlFor="slider-skip-unknown"
                                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border cursor-pointer select-none transition-colors ${sliderSkipped ? 'bg-white border-[#2A807B] text-[#2A807B]' : 'bg-[#2A807B] border-[#2A807B] text-white'}`}
                                >
                                    <input
                                        id="slider-skip-unknown"
                                        type="checkbox"
                                        checked={sliderSkipped}
                                        onChange={(e) => handleSliderSkipToggle(e.target.checked)}
                                        className="w-4 h-4 accent-[#2A807B]"
                                    />
                                    <span className="text-sm font-medium">{lang === 'en' ? "I don't know" : 'ไม่ทราบผล'}</span>
                                </label>
                            </div>
                        )}
                    </React.Fragment>
                ) : null;
            }
			case 'consent':
				return (
					<ConsentQuestion
						config={componentConfig}
						value={answerData}
						onChange={handleAnswerChange}
						onEnterPress={onEnterPress}
					/>
				);
			default:
				return null;
		}
	};

	// Hide mascot for consent questions to show full-width content
	const showMascot = true; // Always show mascot

	return (
		<div className="relative w-full h-full min-h-0 overflow-hidden">
			{/* Mascot - Hidden for consent questions */}
			{showMascot && (
				<div
					id="mascot-question-area-container"
					className="pointer-events-none hidden xl:flex absolute bottom-0 left-0 z-0"
					style={{ transform: `scale(${mascotScale})`, transformOrigin: 'bottom left' }}
				>
					<div id="mascot-layer" className="relative w-[512px] h-[512px] flex items-center justify-center">
						<div
							id="mascot-question-area-container-bg"
							className="absolute left-1/2 top-0 -translate-x-1/2 w-[360px] h-[360px] rounded-full z-0 pointer-events-none"
						/>
						<picture
							id="category-mascot-image"
							className="absolute w-auto h-[512px] -translate-y-16 z-10 md:flex hidden items-center justify-center pointer-events-none"
						>
							<source srcSet={`${getBaseUrl(mascotImageUrl || "")}.avif`} type="image/avif" />
							<source srcSet={`${getBaseUrl(mascotImageUrl || "")}.webp`} type="image/webp" />
							<img
								src={`${getBaseUrl(mascotImageUrl || "")}.png`}
								alt={mascotAlt}
								className="object-contain w-full h-full"
								loading="eager"
							/>
						</picture>
						<div id="category-icon-highlight" className="absolute inset-0 hidden md:block z-20" aria-hidden="true">
								{categoryIconUrls.map((entry, idx) => {
									const pos = iconPositions[idx];
									const url = typeof entry === 'string' ? entry : entry.url;
									const entryStyle = typeof entry === 'object' && 'style' in entry ? entry.style : undefined;
									const override = { ...(entryStyle || {}), ...(categoryIconOverrides[idx] || {}) };
									// Build style allowing override of left/right/transform
									const baseTransform = `translate(-50%, -50%) translate(${pos.x}px, ${pos.y}px)`;
									const style: React.CSSProperties = {
										left: override?.left ?? '50%',
										top: override?.top ?? '50%',
										transform: override?.transform ?? baseTransform,
									};
									return (
										<div
											key={idx}
	                                        id="category-icon-highlight-circle"
											className={`category-icon-highlight-circle-style absolute flex items-center justify-center w-16 h-16 rounded-full shadow-sm transition-all duration-300 ${idx === activeCategoryIndex ? 'active' : 'bg-[#92C8B7]'}`}
											style={style}
										>
	                                        <picture className="w-8 h-8 flex items-center justify-center">
	                                            <source
	                                                srcSet={`${url}.avif`}
	                                                type="image/avif"
	                                            />
	                                            <source
	                                                srcSet={`${url}.webp`}
	                                                type="image/webp"
	                                            />
	                                            <img
	                                                src={`${url}.png`}
	                                                width="44"
	                                                height="44"
	                                                alt="category icon"
	                                                className="object-contain w-full h-full"
	                                                loading="eager"
	                                            />
	                                        </picture>
										</div>
									);
								})}
							</div>
						</div>
					</div>
			)}

			{/* Content */}
			<div
				id="question-content-container"
				className={`relative z-10 w-full h-full min-h-0 flex flex-col gap-6 items-center ${
					questionType === 'consent' 
						? 'px-4 md:px-8 lg:px-12 py-6 md:py-8 max-w-6xl mx-auto' 
						: 'px-6 py-12 overflow-y-auto'
				}`}
			>
                <div id="category-title-area" className="flex justify-center">
                    <h1 
                        className="text-[16px] cursor-pointer hover:opacity-70 transition-opacity duration-200 items-center"
                        onClick={() => handleProgressBarClick(activeCategoryIndex)}
                        title={lang === 'en' ? `Back to category ${activeCategoryIndex + 1}` : `กลับไปที่หมวดที่ ${activeCategoryIndex + 1}`}
                    >
                        {lang === 'en' ? `Category ${activeCategoryIndex + 1}` : `หมวดที่ ${activeCategoryIndex + 1}`} : <strong>{activeCategoryName}</strong>
                    </h1>
                </div>
				<div id="category-progress-bars" className="flex items-center gap-2 w-full min-h-5 max-w-3xl justify-center md:justify-start overflow-hidden">
					<div className="flex flex-1 items-center gap-2">
						{categoryProgressArray.map((pct, i) => {
							const isVisited = categoryVisitedStatus[i];
							const isActive = i === activeCategoryIndex;
							const isClickable = isVisited || i === 0; // Category แรกคลิกได้เสมอ, ที่เหลือต้องเคยเปิดแล้ว
							
							return (
								<div key={i} id={`category-progress-bar-item`} className="flex-1 flex items-center">
									<div
										className={`relative h-2 rounded-full overflow-hidden w-full transition-all duration-200 ${
											isClickable ? 'cursor-pointer hover:h-3' : 'cursor-not-allowed'
										}`}
										style={{ background: '#BBDDD3' }}
										onClick={() => handleProgressBarClick(i)}
										title={isClickable 
											? (lang === 'en' ? `Go to category ${i + 1}` : `ไปที่หมวดที่ ${i + 1}`)
											: (lang === 'en' ? 'Not available yet' : 'ยังไม่สามารถเลือกได้')
										}
									>
										<div
											className="h-full bg-[#2A807B] transition-all duration-300"
											style={{ width: `${pct}%` }}
										/>
									</div>
									{/* Separator between bars (except last) */}
									{i < categoryProgressArray.length - 1 && (
										<div className="w-1 h-2" />
									)}
								</div>
							);
						})}
					</div>
					<span
						id="category-progress-counts"
						className="ml-4 text-xs font-medium text-[#2A807B] whitespace-nowrap"
					>
						{activeCategoryIndex + 1}/{totalCategories}
					</span>
				</div>
				<h2 className={`text-center font-semibold text-[#2E2E2E] text-lg md:text-xl`}>
					{questionText}
				</h2>
				{renderQuestion()}
			</div>
		</div>
	);
}